import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Sparkles } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { imgSrc } from "@/lib/api";

const HERO_FALLBACK = "https://images.unsplash.com/photo-1572021335469-31706a17aaef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHx5b3VuZyUyMGVuZXJnZXRpYyUyMGRpdmVyc2UlMjBzdGFydHVwJTIwdGVhbSUyMG9mZmljZXxlbnwwfHx8fDE3ODcxMDgwNjJ8MA&ixlib=rb-4.1.0&q=85";

const EASE = [0.22, 1, 0.36, 1];

const RevealLine = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = ({ onNav }) => {
  const { content, t, lang } = useSite();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yOrb = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const yCard = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const rotCard = useTransform(scrollYProgress, [0, 1], [3, -2]);

  const hero = content.hero;

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="noise relative min-h-screen flex items-center overflow-hidden bg-[#F8FAFC]">
      {/* parallax art */}
      <motion.div style={{ y: yOrb }} className="absolute -top-32 -right-40 w-[560px] h-[560px] pointer-events-none">
        <img
          src="https://images.unsplash.com/photo-1604079628040-94301bb21b91?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMHBvd2RlciUyMGJsdWUlMjBvcmFuZ2UlMjBncmFkaWVudCUyMG1vZGVybnxlbnwwfHx8fDE3ODcxMDgwNjJ8MA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="w-full h-full object-cover rounded-full blur-[2px] opacity-70"
        />
      </motion.div>
      <div className="absolute -bottom-40 -left-32 w-[420px] h-[420px] rounded-full bg-sky-200/50 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-32 pb-24 grid lg:grid-cols-12 gap-12 items-center w-full">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
            className="inline-flex items-center gap-2 rounded-full bg-white border border-sky-200 px-4 py-1.5 text-xs font-bold tracking-[0.15em] uppercase text-sky-600 shadow-sm"
            data-testid="hero-badge"
          >
            <Sparkles size={13} className="text-orange-500" />
            {t(hero, "badge")}
          </motion.div>

          <h1 className="mt-8 font-display font-black tracking-tighter leading-[1.02] text-5xl sm:text-6xl lg:text-7xl text-slate-900" data-testid="hero-title">
            <RevealLine delay={0.25}>{t(hero, "line1")}</RevealLine>
            <RevealLine delay={0.42} className="text-sky-500">
              {t(hero, "line2")}
              <span className="text-orange-500">.</span>
            </RevealLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
            className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-slate-600"
            data-testid="hero-subtitle"
          >
            {t(hero, "subtitle")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-button"
              onClick={() => onNav("#story")}
              className="group inline-flex items-center gap-2 rounded-full bg-orange-500 px-8 py-4 text-sm font-bold text-white shadow-[0_12px_30px_rgb(249,115,22,0.35)] hover:bg-orange-600 hover:-translate-y-0.5 transition-[background-color,transform,box-shadow] duration-300"
            >
              {t(hero, "cta")}
              <ArrowDown size={16} className="group-hover:translate-y-0.5 transition-transform duration-300" />
            </button>
            <a
              data-testid="hero-contact-button"
              href="/contact"
              className="inline-block rounded-full border-2 border-slate-900 px-8 py-[14px] text-sm font-bold text-slate-900 hover:bg-slate-900 hover:text-white transition-[background-color,color] duration-300"
            >
              {lang === "zh" ? "立即咨询" : "Talk to Us"}
            </a>
          </motion.div>
        </div>

        <motion.div style={{ y: yCard, rotate: rotCard }} className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.92, rotate: 6 }}
            animate={{ opacity: 1, scale: 1, rotate: 3 }}
            transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
            className="relative"
          >
            <div className="absolute -inset-3 rounded-[2.5rem] bg-orange-400/20 rotate-3" />
            <img
              src={imgSrc(hero.image) || HERO_FALLBACK}
              alt="ClearBlue team"
              data-testid="hero-image"
              className="relative rounded-[2rem] object-cover aspect-[4/3] lg:aspect-[4/5] w-full shadow-[0_30px_60px_rgb(15,23,42,0.18)]"
            />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
